import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getCompanyNames } from "../../utilities/DocAssistantUtilities";

function DocAssistantFilterBar({
  setSelectedFilters,
  documentIds,
  document_type,
}) {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countries, setCountries] = useState([]);

  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYearOption, setSelectedYearOption] = useState(null);

  const [categoryThemes, setCategoryThemes] = useState([]);
  const [selectedCategoryTheme, setSelectedCategoryTheme] = useState(null);

  const [dataSources, setDataSources] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState(null);

  const [companyNameMap, setCompanyNameMap] = useState({});

  const showCompanyFilters = documentIds?.[0]?.for_type === "Company";

  const dataSourcName = {
    AR: "Annual Report",
    SR: "Sustainability Report",
    CSR: "CSR Report",
    IR: "Investor Report",
    FR: "Financial Report",
  };

  const formatLabel = (str) =>
    str?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "";

  // Reset all filters when document_type changes
  useEffect(() => {
    setSelectedCountry(null);
    setSelectedYearOption(null);
    setSelectedCategoryTheme(null);
    setSelectedDataSource(null);
    setAvailableYears([]);
    setCategoryThemes([]);
    setDataSources([]);
    setSelectedFilters({
      country: null,
      documentType: null,
      year: null,
      data_source: null,
      category_theme: null,
    });
  }, [document_type]);

  // Populate countries
  useEffect(() => {
    const seen = new Set();
    const options = [];
    for (const doc of documentIds || []) {
      const country = doc.impact_georegions_applicable?.[0];
      if (country && !seen.has(country)) {
        seen.add(country);
        options.push({ value: country, label: formatLabel(country) });
      }
    }
    setCountries(options);
  }, [documentIds]);

  // Populate years only after country is selected
  useEffect(() => {
    if (!selectedCountry) {
      setAvailableYears([]);
      return;
    }

    const yearsSet = new Set();
    for (const doc of documentIds || []) {
      const country = doc.impact_georegions_applicable?.[0];
      if (country !== selectedCountry.value) continue;

      const tags = doc.document_tags || [];
      for (const tag of tags.slice(0, 2)) {
        if (/^\d{4}$/.test(tag)) {
          yearsSet.add(tag);
        }
      }
    }

    const sortedYears = Array.from(yearsSet)
      .map((year) => parseInt(year))
      .sort((a, b) => b - a)
      .map((year) => ({ value: year, label: year.toString() }));

    setAvailableYears(sortedYears);
  }, [selectedCountry, documentIds]);

  // Fetch company names for the country
  useEffect(() => {
    if (selectedCountry?.value) {
      getCompanyNames(selectedCountry.value, (companyArray) => {
        const map = {};
        for (const item of companyArray) {
          map[item.reference_id] = item;
        }
        setCompanyNameMap(map);
      });
    }
  }, [selectedCountry]);

  // Populate category themes only after year is selected
  useEffect(() => {
    if (!selectedCountry || !selectedYearOption) {
      setCategoryThemes([]);
      return;
    }

    const seen = new Set();
    const themeList = [];

    for (const doc of documentIds || []) {
      const country = doc.impact_georegions_applicable?.[0];
      const tags = doc.document_tags || [];
      const yearMatch = selectedYearOption?.value
        ? tags.includes(selectedYearOption.value.toString())
        : false;

      if (country !== selectedCountry.value || !yearMatch) continue;

      const refId = doc.category_theme;
      if (refId && !seen.has(refId)) {
        seen.add(refId);
        const detail = companyNameMap[refId];
        themeList.push({ value: refId, label: detail?.sub_theme || refId });
      }
    }
    setCategoryThemes(themeList);
    if (themeList.length === 1) {
      setSelectedCategoryTheme(themeList[0]);
      setSelectedFilters((prev) => ({
        ...prev,
        category_theme: themeList[0]?.value || null,
        selected_company: themeList[0]?.label || null,
        data_source: null,
      }));
    }
  }, [selectedCountry, selectedYearOption, documentIds, companyNameMap]);

  // Populate data sources only after category theme is selected
  useEffect(() => {
    if (!selectedCountry || !selectedYearOption || !selectedCategoryTheme) {
      setDataSources([]);
      return;
    }

    const seen = new Set();
    const dsList = [];

    for (const doc of documentIds || []) {
      const country = doc.impact_georegions_applicable?.[0];
      const tags = doc.document_tags || [];
      const yearMatch = selectedYearOption?.value
        ? tags.includes(selectedYearOption.value.toString())
        : false;

      if (
        country !== selectedCountry.value ||
        !yearMatch ||
        doc.category_theme !== selectedCategoryTheme.value
      ) {
        continue;
      }

      const ds = doc.data_source;
      if (ds && !seen.has(ds)) {
        seen.add(ds);
        dsList.push({ value: ds, label: dataSourcName[ds[0]] || ds });
      }
    }

    setDataSources(dsList);
    if (dsList.length === 1) {
      setSelectedDataSource(dsList[0]);
      setSelectedFilters((prev) => ({
        ...prev,
        data_source: dsList[0]?.value || null,
      }));
    }
  }, [selectedCountry, selectedYearOption, selectedCategoryTheme, documentIds]);

  // Handlers
  const handleCountryChange = (opt) => {
    setSelectedCountry(opt);
    setSelectedYearOption(null);
    setSelectedCategoryTheme(null);
    setSelectedDataSource(null);
    setSelectedFilters({
      country: opt?.value || null,
      year: null,
      category_theme: null,
      data_source: null,
    });
  };

  const handleYearChange = (opt) => {
    setSelectedYearOption(opt);
    setSelectedCategoryTheme(null);
    setSelectedDataSource(null);
    setSelectedFilters((prev) => ({
      ...prev,
      year: opt?.value || null,
      category_theme: null,
      data_source: null,
    }));
  };

  const handleCategoryThemeChange = (opt) => {
    setSelectedCategoryTheme(opt);
    setSelectedDataSource(null);
    setSelectedFilters((prev) => ({
      ...prev,
      category_theme: opt?.value || null,
      selected_company: opt?.label || null,
      data_source: null,
    }));
  };

  const handleDataSourceChange = (opt) => {
    setSelectedDataSource(opt);
    setSelectedFilters((prev) => ({
      ...prev,
      data_source: opt?.value || null,
    }));
  };

  return (
    <div className="container-fluid p-3 mb-2">
      <div className="row">
        <div className="col-md-3 mb-2">
          <Select
            value={selectedCountry}
            onChange={handleCountryChange}
            options={countries}
            placeholder="Select Country"
            classNamePrefix="select"
          />
        </div>
        <div className="col-md-2 mb-2">
          <Select
            value={selectedYearOption}
            onChange={handleYearChange}
            options={availableYears}
            placeholder="Select Year"
            classNamePrefix="select"
            isDisabled={!selectedCountry}
          />
        </div>

        {showCompanyFilters && (
          <>
            <div className="col-md-3 mb-2">
              <Select
                value={selectedCategoryTheme}
                onChange={handleCategoryThemeChange}
                options={categoryThemes}
                placeholder="Select Company"
                classNamePrefix="select"
                isDisabled={!selectedYearOption}
              />
            </div>
            <div className="col-md-3 mb-2">
              <Select
                value={selectedDataSource}
                onChange={handleDataSourceChange}
                options={dataSources}
                placeholder="Select Report Type"
                classNamePrefix="select"
                isDisabled={!selectedCategoryTheme}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DocAssistantFilterBar;
